import { useState } from "react";
import { useSearchParams } from "react-router";
import { useOwnerVisits, useUsageFeatures, useUsageOwners, useUsagePages, useUsageSummary } from "../api/hooks";
import type { UsageOwnersResponse } from "../../api/contracts";
import { BarChart } from "../components/charts";
import { Badge, Button, EmptyState, ErrorNote, PageHeader, Panel, Spinner, Stat, cx } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { formatAbsolute, formatActiveTime, formatDaysAgo, formatInstant } from "../lib/format";
import { shortDay } from "../lib/charts";
import { DAYS_OPTIONS, parseDays, type Days } from "../lib/analytics";

type OwnerRow = UsageOwnersResponse["owners"][number];
type Tab = "owners" | "features" | "visits";
type Filter = "all" | "notSeen" | "never";
type SortKey = "name" | "live" | "lastSeen" | "daysActive" | "activeMinutes" | "saves";

const TABS: readonly [Tab, string][] = [["owners", "Owners"], ["features", "Features"], ["visits", "Visits"]];
/** Rows are keyed by owner id (e.g. "ut-towns"); that is the label, linked users are a short line under it. */
const ownerLabel = (o: { ownerId: string }) => o.ownerId;
const NAMES_SHOWN = 2;
const shortNames = (names: string[]) =>
  names.length <= NAMES_SHOWN ? names.join(", ") : `${names.slice(0, NAMES_SHOWN).join(", ")} +${names.length - NAMES_SHOWN} more`;

export function UsagePage() {
  const [params, setParams] = useSearchParams();
  const days = parseDays(params.get("days"));
  const [tab, setTab] = useState<Tab>("owners");
  const summary = useUsageSummary(days);
  const s = summary.data;
  return (
    <>
      <PageHeader title="Usage" subtitle="Which franchise owners open the dashboard, what they use, and how long they stay."
        actions={<div role="group" aria-label="Range" className="flex gap-1">{DAYS_OPTIONS.map((d) => <Button key={d} size="sm" variant={d === days ? "primary" : "secondary"} onClick={() => setParams({ days: String(d) })}>{d} days</Button>)}</div>} />
      <ErrorNote error={summary.error} />
      {summary.isPending && <Spinner />}
      {s && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="owners with live locations seen" value={`${s.liveOwnersSeen} / ${s.liveOwners}`} />
          <Stat label="live owners not seen" value={s.liveOwnersNotSeen} tone={s.liveOwnersNotSeen > 0 ? "danger" : "neutral"} />
          <Stat label="median active time" value={s.medianActiveMinutes === null ? "—" : formatActiveTime(s.medianActiveMinutes)} />
        </div>
      )}
      <div role="tablist" aria-label="Usage views" className="mt-4 flex gap-1 border-b border-hs-border">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
            className={cx("-mb-px border-b-2 px-4 py-2 text-sm font-medium", tab === id ? "border-hs-primary text-hs-text" : "border-transparent text-hs-text-muted hover:text-hs-text")}>{label}</button>
        ))}
      </div>
      <div className="mt-4">
        {tab === "owners" && <OwnersTab days={days} />}
        {tab === "features" && <FeaturesTab days={days} />}
        {tab === "visits" && <VisitsTab days={days} />}
      </div>
    </>
  );
}

function sortOwners(rows: OwnerRow[], key: SortKey, dir: "asc" | "desc"): OwnerRow[] {
  const sign = dir === "asc" ? 1 : -1;
  const val = (r: OwnerRow): number | string => {
    switch (key) {
      case "name": return ownerLabel(r).toLowerCase();
      case "live": return r.liveLocations;
      case "daysActive": return r.daysActive;
      case "activeMinutes": return r.activeMinutes;
      case "saves": return r.ruleSavesOwn + r.ruleSavesCorporate;
      case "lastSeen": return r.lastSeenAt ? Date.parse(r.lastSeenAt) : 0;
    }
  };
  return [...rows].sort((a, b) => {
    // Never-seen owners stay at the bottom in either direction of the last seen sort.
    if (key === "lastSeen" && (a.lastSeenAt === null) !== (b.lastSeenAt === null)) return a.lastSeenAt === null ? 1 : -1;
    const x = val(a), y = val(b);
    return (x < y ? -1 : x > y ? 1 : 0) * sign || ownerLabel(a).localeCompare(ownerLabel(b));
  });
}

function OwnersTab({ days }: { days: Days }) {
  const q = useUsageOwners(days);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "lastSeen", dir: "desc" });
  const [open, setOpen] = useState<string | null>(null);
  const now = new Date();
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorNote error={q.error} />;
  // daysActive is the server's UTC-day count for the period, so this chip agrees with the headline number.
  const notSeen = (o: OwnerRow) => o.liveLocations > 0 && o.daysActive === 0;
  const filters: [Filter, string, (o: OwnerRow) => boolean][] = [
    ["all", "All owners", () => true], ["notSeen", `Live, not seen in ${days} days`, notSeen], ["never", "Never seen", (o) => o.lastSeenAt === null],
  ];
  const pass = filters.find((f) => f[0] === filter)![2];
  const rows = sortOwners(q.data.owners.filter(pass), sort.key, sort.dir);
  const click = (key: SortKey) => setSort((p) => (p.key === key ? { key, dir: p.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" ? "asc" : "desc" }));
  const head = (key: SortKey, label: string) => <button type="button" onClick={() => click(key)} className={cx("font-medium", sort.key === key && "text-hs-text underline")}>{label}</button>;
  const sortOf = (key: SortKey) => (sort.key === key ? (sort.dir === "asc" ? "ascending" as const : "descending" as const) : undefined);
  const columns: Column<OwnerRow>[] = [
    { key: "name", header: head("name", "Owner"), sort: sortOf("name"), cell: (o) => (
      <div>
        <button type="button" aria-expanded={open === o.ownerId} onClick={() => setOpen(open === o.ownerId ? null : o.ownerId)} className="text-left"
          title={[...o.names, ...o.contactEmails].join("\n") || undefined}>
          <span data-testid="owner-name" className="font-medium">{ownerLabel(o)}</span>
          {o.names.length > 0 && <span className="block text-xs font-normal text-hs-text-muted">{shortNames(o.names)}</span>}
        </button>
        {open === o.ownerId && <Visits ownerId={o.ownerId} days={days} />}
      </div>
    ) },
    { key: "live", header: head("live", "Live locations"), sort: sortOf("live"), align: "right", cell: (o) => `${o.liveLocations} of ${o.locations}` },
    { key: "lastSeen", header: head("lastSeen", "Last seen"), sort: sortOf("lastSeen"), cell: (o) => o.lastSeenAt === null
      ? <Badge tone="neutral">never</Badge>
      : <span title={formatAbsolute(o.lastSeenAt)}>{formatDaysAgo(o.lastSeenAt, now)}</span> },
    { key: "daysActive", header: head("daysActive", "Days active"), sort: sortOf("daysActive"), align: "right", cell: (o) => o.daysActive },
    { key: "activeMinutes", header: head("activeMinutes", "Active time"), sort: sortOf("activeMinutes"), align: "right", cell: (o) => (o.activeMinutes === 0 ? "—" : formatActiveTime(o.activeMinutes)) },
    { key: "topPages", header: "Top pages", cell: (o) => <span className="text-hs-text-muted">{o.topPages.length ? o.topPages.join(", ") : "—"}</span> },
    { key: "saves", header: head("saves", "Rule saves (own / corporate)"), sort: sortOf("saves"), align: "right", cell: (o) => `${o.ruleSavesOwn} / ${o.ruleSavesCorporate}` },
  ];
  return (
    <>
      <div role="group" aria-label="Owner filter" className="mb-3 flex flex-wrap gap-1">
        {filters.map(([id, label]) => <Button key={id} size="sm" variant={id === filter ? "primary" : "secondary"} aria-pressed={id === filter} onClick={() => setFilter(id)}>{label}</Button>)}
      </div>
      <Panel padding="none">
        <DataTable columns={columns} rows={rows} rowKey={(o) => o.ownerId} density="compact" caption="Owners" emptyState={<EmptyState title="No owners match this filter" />} />
      </Panel>
    </>
  );
}

function Visits({ ownerId, days }: { ownerId: string; days: Days }) {
  const q = useOwnerVisits(ownerId, days);
  if (q.isPending) return <p className="mt-2 text-xs font-normal text-hs-text-muted">Loading visits…</p>;
  if (q.isError) return <ErrorNote error={q.error} />;
  if (q.data.visits.length === 0) return <p className="mt-2 text-xs font-normal text-hs-text-muted">No visits in the last {days} days.</p>;
  return (
    <ul className="mt-2 space-y-1 text-xs font-normal text-hs-text-muted">
      {q.data.visits.map((v) => (
        <li key={v.visitId} className="flex flex-wrap gap-x-3"><span className="tabular-nums">{formatInstant(v.startedAt)}</span><span>{v.pages.join(" → ")}</span><span>{formatActiveTime(v.activeMinutes)} active</span></li>
      ))}
    </ul>
  );
}

const ACTION_LABELS: readonly [string, string][] = [
  ["guide_opened", "Opened the guide"], ["preview_opened", "Opened Preview"], ["support_clicked", "Clicked Support"], ["results_viewed", "Viewed Results"],
];

function FeaturesTab({ days }: { days: Days }) {
  const q = useUsageFeatures();
  const owners = useUsageOwners(days);
  const [open, setOpen] = useState<string | null>(null);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorNote error={q.error} />;
  const nameOf = (id: string) => { const o = owners.data?.owners.find((x) => x.ownerId === id); return o ? ownerLabel(o) : id; };
  const max = Math.max(1, ...q.data.features.map((f) => f.ownersOn));
  return (
    <div className="space-y-4">
      <Panel title="Rules in use" subtitle="Owners with each rule turned on at one or more locations, and who set it. Defaults do not count.">
        <ul className="divide-y divide-hs-border">
          {q.data.features.map((f) => (
            <li key={f.key} className="py-3">
              <button type="button" aria-expanded={open === f.key} onClick={() => setOpen(open === f.key ? null : f.key)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 text-left text-sm">
                <span className="w-48 font-medium">{f.label}</span>
                <span role="img" aria-label={`${f.byOwner} by owner, ${f.byCorporate} by corporate`} className="flex h-2 min-w-[8rem] flex-1 overflow-hidden rounded-full bg-hs-surface-alt">
                  <span className="h-2 bg-hs-accent" style={{ width: `${(f.byOwner / max) * 100}%` }} />
                  <span className="h-2 bg-hs-text-subtle" style={{ width: `${(f.byCorporate / max) * 100}%` }} />
                </span>
                <span className="tabular-nums">{f.ownersOn} owners</span>
                <span className="text-xs text-hs-text-muted">{f.byOwner} by owner, {f.byCorporate} by corporate</span>
              </button>
              {open === f.key && (
                <ul className="mt-2 space-y-1 pl-4 text-xs text-hs-text-muted">
                  {f.owners.length === 0 && <li>No owners yet.</li>}
                  {f.owners.map((o) => <li key={o.ownerId}>{nameOf(o.ownerId)}: on at {o.on} of {o.of} locations, set by {o.credit}</li>)}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Actions" subtitle="Owners who did this in the last 30 days.">
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {ACTION_LABELS.map(([key, label]) => (
            <li key={key} className="flex justify-between gap-3"><span>{label}</span><span className="font-medium tabular-nums">{q.data.actionOwners[key] ?? 0}</span></li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function VisitsTab({ days }: { days: Days }) {
  const q = useUsagePages(days);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorNote error={q.error} />;
  const { pages, daily } = q.data;
  const columns: Column<(typeof pages)[number]>[] = [
    { key: "path", header: "Page", cell: (p) => p.path },
    { key: "visits", header: "Visits", align: "right", cell: (p) => p.visits.toLocaleString() },
    { key: "owners", header: "Owners", align: "right", cell: (p) => p.owners.toLocaleString() },
    { key: "time", header: "Active time", align: "right", cell: (p) => (p.activeMinutes === 0 ? "—" : formatActiveTime(p.activeMinutes)) },
  ];
  return (
    <div className="space-y-4">
      <Panel title="Owners seen per day">
        {daily.length === 0 ? <EmptyState title="No visits recorded in this range" />
          : <BarChart title="Owners seen per day" x={daily.map((d) => d.date)} xTick={shortDay} series={[{ label: "Owners seen", values: daily.map((d) => d.ownersSeen) }]} />}
      </Panel>
      <Panel padding="none">
        <DataTable columns={columns} rows={pages} rowKey={(p) => p.path} density="compact" caption="Visits per page" emptyState={<EmptyState title="No visits recorded in this range" />} />
      </Panel>
    </div>
  );
}
